/**
 * Octopus - AlertRuleDialog (Fase 1000)
 *
 * Diálogo de criar/editar uma regra de alerta, extraído de
 * `EquipmentAlertConfig.js` (mesmos campos/comportamento). Persiste via
 * `alertsApi` (create/update `/alert-rules`). Exporta também
 * `DeleteRuleDialog`, a confirmação de exclusão (AlertDialog) usada junto
 * pelas seções da página (Plans 05/06) — título e corpo verbatim, label de
 * ação "Excluir regra" (constraint do orquestrador da Fase 1000).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Loader2, Mail, MessageCircle, AlertTriangle, WifiOff, TrendingUp, CloudSun, Fuel } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { PhoneListInput, validPhoneList } from '@/components/PhoneNumberInput';
import { alertsApi } from '@/services/alertsApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityOptions = [
  { value: 'low', label: 'Baixa', color: 'text-blue-500' },
  { value: 'medium', label: 'Média', color: 'text-yellow-500' },
  { value: 'high', label: 'Alta', color: 'text-orange-500' },
  { value: 'critical', label: 'Crítica', color: 'text-red-500' },
];

const operatorOptions = [
  { value: '>', label: 'Maior que (>)' },
  { value: '<', label: 'Menor que (<)' },
  { value: '>=', label: 'Maior ou igual (>=)' },
  { value: '<=', label: 'Menor ou igual (<=)' },
  { value: '==', label: 'Igual a (==)' },
];

const categoryLabels = {
  propulsion: 'Propulsão',
  navigation: 'Navegação',
  electrical: 'Elétrico',
  environment: 'Ambiente',
  tanks: 'Tanques',
  steering: 'Direção',
  other: 'Outros',
};

const EMPTY_FORM = {
  name: '',
  alert_type: 'offline',
  enabled: true,
  severity: 'medium',
  repeat_interval_minutes: 5,
  offline_seconds: 300,
  mqtt_topic: '',
  mqtt_field: '',
  source_path: '',
  threshold_operator: '>',
  threshold_value: 0,
  threshold_duration_seconds: 0,
  min_speed_kmh: 10,
  min_duration_seconds: 300,
  forecast_horizon_minutes: 120,
  check_interval_minutes: 60,
  wind_speed_kmh: 35,
  wind_gust_kmh: 50,
  rain_mm: 10,
  ai_instructions: '',
  ai_response_max_chars: 500,
  notify_email: false,
  notify_whatsapp: false,
  notification_emails: '',
  notification_phones: [],
};

const buildFormData = (rule) => {
  if (!rule) return { ...EMPTY_FORM };
  return {
    name: rule.name,
    alert_type: rule.alert_type,
    enabled: rule.enabled,
    severity: rule.severity,
    repeat_interval_minutes: rule.repeat_interval_minutes || 5,
    offline_seconds: rule.offline_seconds || 300,
    mqtt_topic: rule.mqtt_topic || rule.source_path || '',
    mqtt_field: rule.mqtt_field || '',
    source_path: rule.source_path || rule.mqtt_topic || '',
    threshold_operator: rule.threshold_operator || '>',
    threshold_value: rule.threshold_value || 0,
    threshold_duration_seconds: rule.threshold_duration_seconds || 0,
    min_speed_kmh: rule.min_speed_kmh || 10,
    min_duration_seconds: rule.min_duration_seconds || 300,
    forecast_horizon_minutes: rule.forecast_horizon_minutes || 120,
    check_interval_minutes: rule.check_interval_minutes || 60,
    wind_speed_kmh: rule.weather_thresholds?.wind_speed_kmh || rule.operational_limits?.max_wind_kmh || 35,
    wind_gust_kmh: rule.weather_thresholds?.wind_gust_kmh || rule.operational_limits?.max_gust_kmh || 50,
    rain_mm: rule.weather_thresholds?.rain_mm || 10,
    ai_instructions: rule.ai_instructions || '',
    ai_response_max_chars: rule.ai_response_max_chars || 500,
    notify_email: rule.notify_email,
    notify_whatsapp: rule.notify_whatsapp,
    notification_emails: (rule.notification_emails || []).join(', '),
    notification_phones: rule.notification_phones || [],
  };
};

const buildPayload = (formData) => {
  const payload = {
    name: formData.name,
    alert_type: formData.alert_type,
    enabled: formData.enabled,
    severity: formData.severity,
    repeat_interval_minutes: Math.max(5, parseInt(formData.repeat_interval_minutes) || 5),
    notify_email: formData.notify_email,
    notify_whatsapp: formData.notify_whatsapp,
    notification_emails: formData.notification_emails.split(',').map((e) => e.trim()).filter(Boolean),
    notification_phones: validPhoneList(formData.notification_phones),
  };

  if (formData.alert_type === 'offline') {
    payload.offline_seconds = parseInt(formData.offline_seconds) || 300;
  } else if (formData.alert_type === 'threshold') {
    payload.mqtt_topic = formData.mqtt_topic;
    payload.mqtt_field = formData.mqtt_field;
    payload.source_path = formData.source_path || formData.mqtt_topic;
    payload.threshold_operator = formData.threshold_operator;
    payload.threshold_value = parseFloat(formData.threshold_value) || 0;
    payload.threshold_duration_seconds = parseInt(formData.threshold_duration_seconds) || 0;
  } else if (formData.alert_type === 'weather_route') {
    payload.min_speed_kmh = parseFloat(formData.min_speed_kmh) || 10;
    payload.min_duration_seconds = parseInt(formData.min_duration_seconds) || 300;
    payload.forecast_horizon_minutes = parseInt(formData.forecast_horizon_minutes) || 120;
    payload.check_interval_minutes = parseInt(formData.check_interval_minutes) || 60;
    payload.weather_thresholds = {
      wind_speed_kmh: parseFloat(formData.wind_speed_kmh) || 35,
      wind_gust_kmh: parseFloat(formData.wind_gust_kmh) || 50,
      rain_mm: parseFloat(formData.rain_mm) || 10,
    };
    payload.operational_limits = {
      max_wind_kmh: parseFloat(formData.wind_speed_kmh) || 35,
      max_gust_kmh: parseFloat(formData.wind_gust_kmh) || 50,
    };
    payload.ai_instructions = formData.ai_instructions || '';
    payload.ai_response_max_chars = parseInt(formData.ai_response_max_chars) || 500;
  }

  return payload;
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {object|null} props.rule - regra em edição, ou null para criar
 * @param {string} props.equipmentId
 * @param {() => void} [props.onSaved]
 */
const AlertRuleDialog = ({ open, onOpenChange, rule, equipmentId, onSaved }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [formData, setFormData] = useState(() => buildFormData(rule));
  const [saving, setSaving] = useState(false);

  const [availableSources, setAvailableSources] = useState([]);
  const [sourcesByCategory, setSourcesByCategory] = useState({});
  const [loadingSources, setLoadingSources] = useState(false);
  const [sourcesError, setSourcesError] = useState(null);

  useEffect(() => {
    if (open) setFormData(buildFormData(rule));
  }, [open, rule]);

  const fetchSources = useCallback(async () => {
    setLoadingSources(true);
    setSourcesError(null);
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}/source-configs?include_raw=true`);
      const data = response.data;
      setAvailableSources(data.sources || []);
      setSourcesByCategory(data.by_category || {});
    } catch (error) {
      console.error('Error fetching sources:', error);
      setSourcesError('Erro ao carregar dados disponíveis');
    } finally {
      setLoadingSources(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    if (open && formData.alert_type === 'threshold' && availableSources.length === 0 && !loadingSources) {
      fetchSources();
    }
  }, [open, formData.alert_type, availableSources.length, loadingSources, fetchSources]);

  const getSourceLabel = (sourceItem) => {
    const config = sourceItem.config || {};
    const source = sourceItem.source || {};
    const display = sourceItem.display || {};

    const label = config.display_name || source.display_label || source.path;
    const value = display.formatted_value;
    const unit = display.unit || config.display_unit || '';

    if (value !== undefined && value !== null && value !== '') {
      return `${label} (${value}${unit ? ' ' + unit : ''})`;
    }
    return label;
  };

  const isTopicInSources = (topic) => availableSources.some((s) => s.source?.path === topic);

  const handleTopicSelect = (path) => {
    setFormData((prev) => ({
      ...prev,
      mqtt_topic: path,
      source_path: path,
      mqtt_field: 'value',
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { equipment_id: equipmentId, ...buildPayload(formData) };
      if (rule) {
        await alertsApi.updateRule(rule.id, payload);
        toast.success('Regra de alerta atualizada');
      } else {
        await alertsApi.createRule(payload);
        toast.success('Regra de alerta criada');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      console.error('Error saving alert rule:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar regra');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[calc(100dvh-2rem)] p-0 overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="px-6 pt-6">
            {rule ? 'Editar Regra de Alerta' : 'Nova Regra de Alerta'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 px-6 py-4 overflow-y-auto min-h-0">
          <div className="space-y-2">
            <Label>Nome da Regra</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Alerta de RPM alto"
              data-testid="alert-rule-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Alerta</Label>
              <Select
                value={formData.alert_type}
                onValueChange={(value) => setFormData({ ...formData, alert_type: value })}
              >
                <SelectTrigger data-testid="alert-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="offline">
                    <div className="flex items-center gap-2">
                      <WifiOff className="w-4 h-4" /> Offline
                    </div>
                  </SelectItem>
                  <SelectItem value="threshold">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Threshold MQTT
                    </div>
                  </SelectItem>
                  <SelectItem value="weather_route">
                    <div className="flex items-center gap-2">
                      <CloudSun className="w-4 h-4" /> Previsão em Rota
                    </div>
                  </SelectItem>
                  <SelectItem value="fuel_reserve">
                    <div className="flex items-center gap-2">
                      <Fuel className="w-4 h-4" /> Reserva de combustível
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Severidade</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) => setFormData({ ...formData, severity: value })}
              >
                <SelectTrigger data-testid="alert-severity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {severityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className={opt.color}>{opt.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.alert_type !== 'weather_route' && (
            <div className="space-y-2">
              <Label>Intervalo de repetição (min)</Label>
              <Input
                type="number"
                min="5"
                value={formData.repeat_interval_minutes}
                onChange={(e) => setFormData({ ...formData, repeat_interval_minutes: e.target.value })}
                data-testid="repeat-interval-input"
              />
              <p className="text-xs text-muted-foreground">
                Evita repetir o mesmo alerta antes deste intervalo. Mínimo de 5 minutos.
              </p>
            </div>
          )}

          {formData.alert_type === 'offline' && (
            <div className="space-y-2">
              <Label>Tempo offline (segundos)</Label>
              <Input
                type="number"
                value={formData.offline_seconds}
                onChange={(e) => setFormData({ ...formData, offline_seconds: e.target.value })}
                placeholder="300"
                data-testid="offline-seconds-input"
              />
              <p className="text-xs text-muted-foreground">
                Alerta será disparado quando equipamento ficar offline por este tempo
              </p>
            </div>
          )}

          {formData.alert_type === 'threshold' && (
            <>
              {loadingSources && (
                <div className="flex items-center gap-2 text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Carregando dados disponíveis...</span>
                </div>
              )}

              {sourcesError && (
                <div className="flex items-center gap-2 text-destructive py-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{sourcesError}</span>
                </div>
              )}

              {!loadingSources && !sourcesError && availableSources.length === 0 && (
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                  <p className="text-sm text-muted-foreground">
                    Nenhum dado disponível para este equipamento.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Aguarde o equipamento enviar dados para configurar alertas de threshold.
                  </p>
                </div>
              )}

              {!loadingSources && availableSources.length > 0 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dado a Monitorar</Label>
                      <Select value={formData.mqtt_topic} onValueChange={handleTopicSelect}>
                        <SelectTrigger data-testid="mqtt-topic-select">
                          <SelectValue placeholder="Selecione um dado..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Object.keys(sourcesByCategory).sort().map((category) => (
                            <SelectGroup key={category}>
                              <SelectLabel className="text-xs uppercase text-muted-foreground">
                                {categoryLabels[category] || category}
                              </SelectLabel>
                              {sourcesByCategory[category].map((sourceItem) => (
                                <SelectItem
                                  key={sourceItem.source?.path}
                                  value={sourceItem.source?.path || ''}
                                >
                                  {getSourceLabel(sourceItem)}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}

                          {formData.mqtt_topic && !isTopicInSources(formData.mqtt_topic) && (
                            <SelectGroup>
                              <SelectLabel className="text-xs uppercase text-yellow-500">
                                Source não encontrado
                              </SelectLabel>
                              <SelectItem value={formData.mqtt_topic} className="text-yellow-600">
                                {formData.mqtt_topic} (legado)
                              </SelectItem>
                            </SelectGroup>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Campo</Label>
                      <Select
                        value={formData.mqtt_field || 'value'}
                        onValueChange={(value) => setFormData({ ...formData, mqtt_field: value })}
                        disabled={!formData.mqtt_topic}
                      >
                        <SelectTrigger data-testid="mqtt-field-select">
                          <SelectValue placeholder="Selecione um campo..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="value">value (padrão)</SelectItem>
                          {formData.mqtt_topic?.includes('position') && (
                            <>
                              <SelectItem value="latitude">latitude</SelectItem>
                              <SelectItem value="longitude">longitude</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Para dados escalares, use "value"
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Operador</Label>
                      <Select
                        value={formData.threshold_operator}
                        onValueChange={(value) => setFormData({ ...formData, threshold_operator: value })}
                      >
                        <SelectTrigger data-testid="threshold-operator-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {operatorOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Valor Limite</Label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.threshold_value}
                        onChange={(e) => setFormData({ ...formData, threshold_value: e.target.value })}
                        placeholder="1300"
                        data-testid="threshold-value-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Duração (seg)</Label>
                      <Input
                        type="number"
                        value={formData.threshold_duration_seconds}
                        onChange={(e) => setFormData({ ...formData, threshold_duration_seconds: e.target.value })}
                        placeholder="0 = instantâneo"
                        data-testid="threshold-duration-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Duração = 0 significa alerta instantâneo quando o valor exceder o limite
                  </p>
                </>
              )}
            </>
          )}

          {formData.alert_type === 'weather_route' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Velocidade mínima (km/h)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.min_speed_kmh}
                    onChange={(e) => setFormData({ ...formData, min_speed_kmh: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duração mínima (seg)</Label>
                  <Input
                    type="number"
                    value={formData.min_duration_seconds}
                    onChange={(e) => setFormData({ ...formData, min_duration_seconds: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horizonte da previsão (min)</Label>
                  <Input
                    type="number"
                    value={formData.forecast_horizon_minutes}
                    onChange={(e) => setFormData({ ...formData, forecast_horizon_minutes: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Intervalo após 1º alerta (min)</Label>
                  <Input
                    type="number"
                    value={formData.check_interval_minutes}
                    onChange={(e) => setFormData({ ...formData, check_interval_minutes: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vento limite (km/h)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.wind_speed_kmh}
                    onChange={(e) => setFormData({ ...formData, wind_speed_kmh: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rajada limite (km/h)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.wind_gust_kmh}
                    onChange={(e) => setFormData({ ...formData, wind_gust_kmh: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Chuva limite (mm)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.rain_mm}
                    onChange={(e) => setFormData({ ...formData, rain_mm: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Instruções para avaliação da IA</Label>
                <Textarea
                  value={formData.ai_instructions}
                  maxLength={2000}
                  rows={4}
                  onChange={(e) => setFormData({ ...formData, ai_instructions: e.target.value })}
                  placeholder="Ex: considerar limite operacional do equipamento, tipo de operação, vento máximo permitido, orientação de linguagem e critérios de risco."
                />
                <p className="text-xs text-muted-foreground">
                  {formData.ai_instructions.length}/2000 caracteres. A IA usa estas instruções apenas para escrever a AVALIAÇÃO; os dados de chuva e tempestade vêm da previsão.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Limite da avaliação (caracteres)</Label>
                <Input
                  type="number"
                  min="100"
                  max="2000"
                  value={formData.ai_response_max_chars}
                  onChange={(e) => setFormData({ ...formData, ai_response_max_chars: e.target.value })}
                />
              </div>
            </div>
          )}

          <Separator className="my-2" />

          <div className="space-y-4">
            <h4 className="font-medium">Notificações</h4>
            <p className="text-xs text-muted-foreground italic">
              Todos os usuários cadastrados serão notificados automaticamente
            </p>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <Label>Notificar por Email</Label>
              </div>
              <Switch
                checked={formData.notify_email}
                onCheckedChange={(checked) => setFormData({ ...formData, notify_email: checked })}
                data-testid="notify-email-switch"
              />
            </div>

            {formData.notify_email && (
              <div className="space-y-2 ml-6">
                <Label className="text-sm">
                  Destinatários adicionais {!isAdmin && <span className="text-yellow-500 text-xs">(somente admin)</span>}
                </Label>
                <Input
                  value={formData.notification_emails}
                  onChange={(e) => setFormData({ ...formData, notification_emails: e.target.value })}
                  placeholder={isAdmin ? 'email1@empresa.com, email2@empresa.com' : 'Somente admin pode editar'}
                  disabled={!isAdmin}
                  className={cn(!isAdmin && 'opacity-50 cursor-not-allowed')}
                  data-testid="notification-emails-input"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-muted-foreground" />
                <Label>Notificar por WhatsApp</Label>
              </div>
              <Switch
                checked={formData.notify_whatsapp}
                onCheckedChange={(checked) => setFormData({ ...formData, notify_whatsapp: checked })}
                data-testid="notify-whatsapp-switch"
              />
            </div>

            {formData.notify_whatsapp && (
              <div className="space-y-2 ml-6">
                <Label className="text-sm">
                  Destinatários adicionais {!isAdmin && <span className="text-yellow-500 text-xs">(somente admin)</span>}
                </Label>
                <PhoneListInput
                  value={formData.notification_phones}
                  onChange={(phones) => setFormData({ ...formData, notification_phones: phones })}
                  disabled={!isAdmin}
                  idPrefix="notification-phones"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-background">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-alert-rule-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Confirmação de exclusão de regra — AlertDialog dedicado (não embutido no
 * dialog de edição), reusado pelas seções da página nos Plans 05/06.
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {object|null} props.rule
 * @param {(rule: object) => void} props.onConfirm
 */
export const DeleteRuleDialog = ({ open, onOpenChange, rule, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Excluir Regra de Alerta</AlertDialogTitle>
        <AlertDialogDescription>
          Tem certeza que deseja excluir esta regra? Esta ação não pode ser desfeita.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction onClick={() => onConfirm?.(rule)} className="bg-destructive hover:bg-destructive/90">
          Excluir regra
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default AlertRuleDialog;
